import '../components/CompactSwitch.css';
import React from 'react';
import CodiceFiscale from 'codice-fiscale-js';
import { FaUser, FaIdCard, FaMapMarkerAlt, FaBuilding, FaEnvelope, FaPhone, FaCity, FaCopy, FaEuroSign, FaPlus, FaTimes, FaWhatsapp } from 'react-icons/fa';
import ComuneAutocomplete from '../components/ComuneAutocomplete';
import QuickAddressInput from '../components/QuickAddressInput';
import PracticeDocumentLinks from '../components/PracticeDocumentLinks';
import useCopyFeedback from '../hooks/useCopyFeedback';
import './DatiCliente.css';

function DatiCliente({ data = {}, setData, schedaId, tipoPratica, showDocumentLinks = true }) {
  const [cfMessage, setCfMessage] = React.useState('');
  
  // Safe default values
  const formData = {
    clienteTipo: 'fisica',
    cognome: '',
    nome: '',
    codice_fiscale: '',
    data_nascita: '',
    luogo_nascita: '',
    natoEstero: false,
    statoNascita: '',
    ragione_sociale: '',
    partita_iva: '',
    codice_fiscale_azienda: '',
    sede_legale: '',
    email: '',
    telefono: '',
    telefonoWhatsapp: false,
    emailsExtra: [],
    telefoniExtra: [],
    indirizzo: '',
    citta: '',
    cap: '',
    provincia: '',
    occupazione: '',
    iban: '',
    ...data
  };
  const { copiedValue: copiedIban, copyValue } = useCopyFeedback();

  const handleChange = (field, value) => {
    setData(prev => ({ ...prev, [field]: value }));
  };
  const formatProvincia = value => value ? value.charAt(0).toUpperCase() + value.slice(1).toLowerCase() : '';
  const selectComuneIndirizzo = comune => setData(prev => ({ ...prev,
    citta: comune.nome,
    provincia: formatProvincia(comune.provincia || comune.sigla || ''),
    cap: comune.cap?.length === 1 ? comune.cap[0] : (prev.cap || '')
  }));
  const addExtra = (field, tipo) => setData(prev => ({ ...prev,
    [field]: [...(prev[field] || []), { tipo, valore: '', ...(field === 'telefoniExtra' ? { whatsapp: false } : {}) }]
  }));
  const updateExtra = (field, index, patch) => setData(prev => ({ ...prev,
    [field]: (prev[field] || []).map((item, position) => position === index ? { ...item, ...patch } : item)
  }));
  const removeExtra = (field, index) => setData(prev => ({ ...prev,
    [field]: (prev[field] || []).filter((_, position) => position !== index)
  }));

  // Formattazione automatica per alcuni campi
  const genderFromFiscalCode = value => {
    const code = String(value || '').toUpperCase();
    if (!/^[A-Z0-9]{16}$/.test(code)) return '';
    return Number(code.slice(9, 11)) > 40 ? 'F' : 'M';
  };
  const handleSpecialChange = (field, value) => {
    let formattedValue = value;
    
    if (field === 'codiceFiscale' || field === 'iban') {
      formattedValue = value.toUpperCase();
    } else if (field === 'cognome' || field === 'nome') {
      formattedValue = value
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
    }
    
    if (field === 'codiceFiscale') setData(prev => ({ ...prev, codiceFiscale: formattedValue,
      sesso: genderFromFiscalCode(formattedValue) || prev.sesso || '' }));
    else handleChange(field, formattedValue);
  };

  const handleTipoClienteChange = (tipo) => {
    if (tipo === 'fisica') {
      setData({
        ...formData,
        clienteTipo: 'fisica',
        ragioneSociale: '',
        partitaIva: '',
        codiceFiscaleAzienda: '',
        sedeLegale: ''
      });
    } else {
      setData({
        ...formData,
        clienteTipo: 'giuridica',
        cognome: '',
        nome: '',
        codiceFiscale: '',
        dataNascita: '',
        luogoNascita: ''
      });
    }
  };

  const calcolaCodiceFiscale = () => {
    const { nome, cognome, dataNascita, luogoNascita, natoEstero, statoNascita, sesso } = formData;
    const luogoPerCalcolo = natoEstero ? statoNascita : luogoNascita?.replace(/\s*\([A-Z]{2}\)\s*$/i, '');
    if (!nome || !cognome || !dataNascita || !luogoNascita || !luogoPerCalcolo || !sesso) {
      setCfMessage('Completa nome, cognome, luogo e data di nascita e sesso.');
      return;
    }
    try {
      const [year, month, day] = dataNascita.split('-').map(Number);
      const codice = CodiceFiscale.compute({ name: nome, surname: cognome, gender: sesso,
        day, month, year, birthplace: luogoPerCalcolo.trim() });
      setData(prev => ({ ...prev, codiceFiscale: codice }));
      setCfMessage('Codice proposto: puoi correggerlo direttamente nel campo. In caso di omocodia usa quello assegnato dall’Agenzia delle Entrate.');
    } catch (_) {
      setCfMessage('Luogo di nascita non riconosciuto. Controlla il nome del comune o dello Stato estero, oppure inserisci il codice fiscale manualmente.');
    }
  };

  React.useEffect(() => {
    if (formData.clienteTipo !== 'fisica' || formData.codiceFiscale || !formData.nome || !formData.cognome ||
      !formData.dataNascita || !formData.luogoNascita || !formData.sesso || (formData.natoEstero && !formData.statoNascita)) return;
    calcolaCodiceFiscale();
    // Il codice viene proposto una sola volta; una modifica manuale non viene mai sovrascritta.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.clienteTipo, formData.nome, formData.cognome, formData.dataNascita, formData.luogoNascita,
    formData.sesso, formData.natoEstero, formData.statoNascita, formData.codiceFiscale]);

  return (
    <div className="dati-cliente-container">
      <h2>Dati Cliente</h2>

      <div className="cliente-tipo">
        <label>
          <input
            type="radio"
            value="fisica"
            checked={formData.clienteTipo === 'fisica'}
            onChange={() => handleTipoClienteChange('fisica')}
          />
          Persona Fisica
        </label>
        <label>
          <input
            type="radio"
            value="giuridica"
            checked={formData.clienteTipo === 'giuridica'}
            onChange={() => handleTipoClienteChange('giuridica')}
          />
          Persona Giuridica
        </label>
      </div>

      <div className="cliente-layout">
      <div className="cliente-column">
      {formData.clienteTipo === 'fisica' ? (
        <div className="box">
          <h3>Dati Personali</h3>
          <div className="box-content">
            <div className="input-with-icon labeled-client-field" data-label="Cognome">
              <FaUser className="input-icon" />
              <input
                type="text"
                placeholder="Cognome"
                value={formData.cognome || ''}
                onChange={(e) => handleSpecialChange('cognome', e.target.value)}
                required
              />
            </div>

            <div className="input-with-icon labeled-client-field" data-label="Nome">
              <FaUser className="input-icon" />
              <input
                type="text"
                placeholder="Nome"
                value={formData.nome || ''}
                onChange={(e) => handleSpecialChange('nome', e.target.value)}
                required
              />
            </div>

            <div className="input-with-icon labeled-client-field" data-label="Luogo di nascita">
              <FaMapMarkerAlt className="input-icon" />
              {formData.natoEstero ? <input type="text" placeholder="Città estera di nascita" value={formData.luogoNascita || ''} onChange={event => handleChange('luogoNascita', event.target.value)} required />
                : <ComuneAutocomplete includeProvince placeholder="Luogo di Nascita" value={formData.luogoNascita || ''} onChange={value => handleChange('luogoNascita', value)} required />}
            </div>

            <div className="cf-controls foreign-birth-controls">
              <label><input type="checkbox" checked={Boolean(formData.natoEstero)} onChange={event => setData(previous => ({ ...previous, natoEstero: event.target.checked, statoNascita: event.target.checked ? (previous.statoNascita || '') : '' }))} /> Nato all’estero</label>
              {formData.natoEstero && <input type="text" placeholder="Stato estero di nascita (es. Romania)" value={formData.statoNascita || ''} onChange={event => handleChange('statoNascita', event.target.value)} required />}
            </div>

            <div className="input-with-icon labeled-client-field" data-label="Data di nascita">
              <FaIdCard className="input-icon" />
              <input
                type="date"
                aria-label="Data di nascita"
                value={formData.dataNascita || ''}
                onChange={(e) => handleChange('dataNascita', e.target.value)}
                required
              />
            </div>
            <div className="cf-controls">
              <label htmlFor="cliente-sesso">Sesso anagrafico per il codice fiscale</label>
              <select id="cliente-sesso" value={formData.sesso || ''} onChange={e => handleChange('sesso', e.target.value)}>
                <option value="">Seleziona</option><option value="F">F</option><option value="M">M</option>
              </select>
            </div>
            <div className="cf-controls">
              <label htmlFor="cliente-cf">Codice fiscale</label>
              <div className="cf-input-row">
                <input id="cliente-cf" type="text" maxLength={16} value={formData.codiceFiscale || ''}
                  onChange={e => handleSpecialChange('codiceFiscale', e.target.value)} />
              </div>
              {cfMessage && <small role="status">{cfMessage}</small>}
            </div>
            <div className="input-with-icon labeled-client-field" data-label="Occupazione">
              <FaUser className="input-icon" />
              <input type="text" placeholder="Occupazione" value={formData.occupazione || ''}
                onChange={e => handleChange('occupazione', e.target.value)} />
            </div>
            {showDocumentLinks && <PracticeDocumentLinks schedaId={schedaId} title="Documenti personali" documentKeys={tipoPratica === 'RC diversi' ? ['identityCard'] : ['identityCard', 'drivingLicense']} />}
          </div>
        </div>
      ) : (
        <div className="box">
          <h3>Dati Aziendali</h3>
          <div className="box-content">
            <div className="input-with-icon labeled-client-field" data-label="Ragione sociale">
              <FaBuilding className="input-icon" />
              <input
                type="text"
                placeholder="Ragione Sociale"
                value={formData.ragioneSociale}
                onChange={(e) => handleChange('ragioneSociale', e.target.value)}
                required
              />
            </div>

            <div className="input-with-icon labeled-client-field" data-label="Partita IVA">
              <FaIdCard className="input-icon" />
              <input
                type="text"
                placeholder="Partita IVA"
                value={formData.partitaIva}
                onChange={(e) => handleChange('partitaIva', e.target.value)}
                required
              />
            </div>

            <div className="input-with-icon labeled-client-field" data-label="Codice fiscale azienda">
              <FaIdCard className="input-icon" />
              <input
                type="text"
                placeholder="Codice Fiscale Azienda"
                value={formData.codiceFiscaleAzienda}
                onChange={(e) => handleChange('codiceFiscaleAzienda', e.target.value)}
                required
              />
            </div>

            <div className="input-with-icon labeled-client-field" data-label="Sede legale">
              <FaMapMarkerAlt className="input-icon" />
              <input
                type="text"
                placeholder="Sede Legale"
                value={formData.sedeLegale}
                onChange={(e) => handleChange('sedeLegale', e.target.value)}
                required
              />
            </div>
          </div>
        </div>
      )}

      <div className="box">
        <h3>Dati Bancari</h3>
        <div className="box-content">
          <div className="input-with-icon labeled-client-field copy-enabled" data-label="IBAN">
            <FaEuroSign className="input-icon" />
            <input
              type="text"
              placeholder="IBAN"
              value={formData.iban}
              onChange={(e) => handleSpecialChange('iban', e.target.value)}
              required
            />
            {formData.iban && <button type="button" className="field-copy-button" title={copiedIban === formData.iban ? 'Copiato' : 'Copia IBAN'} onClick={() => copyValue(formData.iban)}><FaCopy /></button>}
          </div>
        </div>
      </div>
      </div>

      <div className="cliente-column">

      <div className="box">
        <h3>Indirizzo</h3>
        <div className="box-content">
          <QuickAddressInput addressValue={formData.indirizzo || ''} onApply={address => setData(previous => ({ ...previous, ...address }))} />
          <div className="input-with-icon labeled-client-field" data-label="Città">
            <FaCity className="input-icon" />
            <ComuneAutocomplete value={formData.citta || ''} onChange={value => handleChange('citta', value)}
              onSelect={selectComuneIndirizzo} required />
          </div>
          <div className="input-with-icon labeled-client-field" data-label="CAP">
            <FaMapMarkerAlt className="input-icon" />
            <input type="text" placeholder="CAP" value={formData.cap || ''}
              onChange={(e) => handleChange('cap', e.target.value)} required />
          </div>
          <div className="input-with-icon labeled-client-field" data-label="Provincia">
            <FaMapMarkerAlt className="input-icon" />
            <input type="text" placeholder="Provincia" value={formData.provincia || ''}
              onChange={(e) => handleChange('provincia', formatProvincia(e.target.value))} required />
          </div>
        </div>
      </div>

      <div className="box">
        <div className="recapiti-heading">
          <h3>Contatti</h3>
          <div className="recapiti-add-actions">
            <button type="button" aria-label="Aggiungi email o PEC" onClick={() => addExtra('emailsExtra', 'Email')}><FaPlus /> Email / PEC</button>
            <button type="button" aria-label="Aggiungi telefono" onClick={() => addExtra('telefoniExtra', 'Cellulare')}><FaPlus /> Telefono</button>
          </div>
        </div>
        <div className="box-content">
          <div className="input-with-icon labeled-client-field" data-label="Email">
            <FaEnvelope className="input-icon" />
            <input
              type="email"
              placeholder="Email"
              value={formData.email || ''}
              onChange={(e) => handleChange('email', e.target.value.toLocaleLowerCase('it-IT'))}
              required
            />
          </div>

          <div className="recapito-phone-row recapito-phone-main">
            <div className="input-with-icon recapito-phone-field">
              <FaPhone className="input-icon" aria-hidden="true" />
              <input type="tel" aria-label="Telefono" placeholder="Numero di telefono" value={formData.telefono || ''}
                onChange={(e) => handleChange('telefono', e.target.value)} required />
            </div>
            <label className="cisa-switch recapito-whatsapp" title="Disponibile su WhatsApp">
              <FaWhatsapp /><input type="checkbox" role="switch" aria-label="Disponibile su WhatsApp" checked={Boolean(formData.telefonoWhatsapp)}
                onChange={e => handleChange('telefonoWhatsapp', e.target.checked)} /><i aria-hidden="true"/><b>WhatsApp</b>
            </label>
          </div>
          {(formData.emailsExtra || []).map((item, index) => <div className="recapito-extra" key={`email-${index}`}>
            <select aria-label={`Tipo email ${index + 2}`} value={item.tipo} onChange={e => updateExtra('emailsExtra', index, { tipo: e.target.value })}>
              <option>Email</option><option>PEC</option><option>Lavoro</option><option>Altro</option>
            </select>
            <input type="email" aria-label={`Email aggiuntiva ${index + 2}`} placeholder="Indirizzo email" value={item.valore}
              onChange={e => updateExtra('emailsExtra', index, { valore: e.target.value.toLocaleLowerCase('it-IT') })} />
            <button type="button" className="recapito-remove" aria-label={`Rimuovi email ${index + 2}`}
              onClick={() => removeExtra('emailsExtra', index)}><FaTimes /></button>
          </div>)}
          {(formData.telefoniExtra || []).map((item, index) => <div className="recapito-extra recapito-phone-row" key={`telefono-${index}`}>
            <select aria-label={`Tipo telefono ${index + 2}`} value={item.tipo} onChange={e => updateExtra('telefoniExtra', index, { tipo: e.target.value })}>
              <option>Cellulare</option><option>Casa</option><option>Ufficio</option><option>Altro</option>
            </select>
            <input type="tel" aria-label={`Telefono aggiuntivo ${index + 2}`} placeholder="Numero di telefono" value={item.valore}
              onChange={e => updateExtra('telefoniExtra', index, { valore: e.target.value })} />
            <label className="cisa-switch recapito-whatsapp" title="Disponibile su WhatsApp">
              <FaWhatsapp /><input type="checkbox" role="switch" aria-label={`WhatsApp telefono ${index + 2}`} checked={Boolean(item.whatsapp)}
                onChange={e => updateExtra('telefoniExtra', index, { whatsapp: e.target.checked })} /><i aria-hidden="true"/><b>WhatsApp</b>
            </label>
            <button type="button" className="recapito-remove" aria-label={`Rimuovi telefono ${index + 2}`}
              onClick={() => removeExtra('telefoniExtra', index)}><FaTimes /></button>
          </div>)}
        </div>
      </div>

      </div>
      </div>
    </div>
  );
}

export default DatiCliente;
